const { strings } = window.theme

class TagalysLibrary {
  /**
   * Build a market-aware page URL from handle.
   * @param {string} handle
   * @returns {string}
   */
  static getPageUrl (handle) {
    return `${window.BAO.Utils.getRootPrefix()}pages/${handle}`
  }

  /**
   * Build a market-aware product URL from handle (Tagalys 2.x+).
   * @param {object} product
   * @returns {string}
   */
  static getProductLink (product) {
    if (!product?.handle) return ''
    return `${window.BAO.Utils.getRootPrefix()}products/${product.handle}`
  }

  static getSearchResultsUrl (query) {
    const path = `${window.BAO.Utils.getRootPrefix()}pages/search-results`
    if (!query) return path
    return `${path}?query=${encodeURIComponent(query)}`
  }

  /**
   * Primary image URL for Tagalys 2.x+ (featured_image may be null).
   * @param {object} product
   * @returns {string}
   */
  static getProductImageUrl (product) {
    return product?.featured_image?.src || product?.images?.[0]?.src || ''
  }

  static getSearchResultsCount (helpers) {
    const stored = window.theme?.tagalys?.searchSuggestionsTotal
    if (stored != null && stored !== '') return stored

    const pageSize = helpers?.getProducts?.()?.length || 0
    if (typeof helpers?.getTotalProductsCount === 'function') {
      const total = Number(helpers.getTotalProductsCount())
      if (!Number.isNaN(total) && total > pageSize) return total
    }
    return pageSize
  }

  static getProductTitle (product) {
    const cleanTitle =
      product.metafields?.details?.clean_title?.value ||
      product.metafields?.details?.clean_title

    if (typeof cleanTitle === 'string' && cleanTitle.trim()) {
      return cleanTitle
    }

    return product.title || product.name || ''
  }

  /**
   * Colour name from Tagalys metafields. `colours.title` is a single-line
   * string; treating it as an array (`value[0]`) shows only the first letter.
   * @param {object} product
   * @returns {string}
   */
  static getColourTitle (product) {
    const field = product?.metafields?.colours?.title
    if (field == null || field === '') return ''

    let raw = field
    if (typeof field === 'object' && !Array.isArray(field)) {
      raw = field.value !== undefined ? field.value : field
    }

    if (Array.isArray(raw)) {
      raw = raw[0]
    }

    if (typeof raw !== 'string') return ''

    return raw.trim()
  }

  /**
   * Size option fieldset marked in Liquid (`data-product-option="size"`).
   * @param {string} [productId]
   * @returns {Element|null}
   */
  static getSizeOptionElement (productId) {
    const selector = '[data-product-option="size"]'
    if (productId) {
      const scoped = document.querySelector(
        `${selector}[data-product-id="${productId}"]`,
      )
      if (scoped) return scoped
    }

    return document.querySelector(selector)
  }

  /**
   * 1-based option position from the size fieldset data attribute.
   * @param {string} [productId]
   * @returns {number}
   */
  static getSizeOptionPosition (productId) {
    const position = Number(
      TagalysLibrary.getSizeOptionElement(productId)?.dataset.optionPosition,
    )
    return position > 0 ? position : 1
  }

  /**
   * Selected size value from the marked size option.
   * @param {string} [productId]
   * @returns {string}
   */
  static getSelectedSize (productId) {
    const fieldset = TagalysLibrary.getSizeOptionElement(productId)
    if (!fieldset) return ''

    const checked = fieldset.querySelector('input:checked')
    if (checked?.value) return checked.value

    const select = fieldset.querySelector('select')
    return select?.value || ''
  }

  /**
   * Variant that matches a size value. Prefers in-stock, then any match.
   * Tagalys catalogue availability is often stale on secondary markets.
   * @param {object} product
   * @param {string} size
   * @param {number} [position=1]
   * @returns {object|null}
   */
  static getVariantForSize (product, size, position = 1) {
    if (!size || !product?.variants) return null

    const optionKey = `option${position}`
    const matches = product.variants.filter(
      variant => variant[optionKey] === size,
    )

    return matches.find(variant => variant.available) || matches[0] || null
  }

  static formatResultsWithCount (count) {
    return (
      window.theme.strings?.tagalys?.resultsWithCount || '[count]'
    ).replace('[count]', String(count ?? ''))
  }

  static typeSuggestionsRender (html, args) {
    const props = args.props
    const helpers = props.helpers
    const textSuggestions = helpers.getTextSuggestions()

    const classes = ['sec-Section_TagalysSlides', 'tag-TypeSuggestions']
    if (helpers.hasTextSearchSuggestions() > 0) {
      classes.push('tag-TypeSuggestions-producttext')
    } else {
      classes.push('tag-TypeSuggestions-product')
    }
    const className = classes.join(' ')

    if (helpers.hasTextSearchSuggestions()) {
      const searchQueries = []
      const query = helpers.getEnteredSearchQuery() || ''
      textSuggestions[0].items.forEach(item => {
        const label = item.title || item.query || ''
        const href = window.BAO.Utils.withRootPrefix(
          item.link ||
            (item.query ? helpers.getLink(item.query) : item.queryString || '#'),
        )

        searchQueries.push(`
            <li class="fz-15_28">
              <a class="tag-SearchSuggestions_Suggestion" href="${href}">${label
          .replace(new RegExp(query, 'ig'), `<b>${query}</b>`)
          .replace(/^./, label.charAt(0).toUpperCase())}</a>
            </li>
          `)
      })

      const targetNode = document.querySelector(
        '[data-tagalys-search-el="suggestions"]',
      )
      targetNode.replaceChildren()
      targetNode.insertAdjacentHTML(
        'beforeend',
        `
          ${searchQueries.join(' ')}
        `,
      )
    }

    const resultCount = helpers.getProducts().length
    const resultsLabel = TagalysLibrary.formatResultsWithCount(
      TagalysLibrary.getSearchResultsCount(helpers),
    )

    return html`
      <div class="tag-Title_Header">
        ${resultCount > 0
          ? html`
              <span
                class="tag-SearchSuggestions_Heading fz-15_19 tag-ResultTotal"
              >
                ${resultsLabel}
              </span>
              <div class="tag-Title_ViewAll">
                <a
                  href="${TagalysLibrary.getSearchResultsUrl(
                    helpers.getEnteredSearchQuery(),
                  )}"
                  target="_parent"
                  class="tag-Title_Button fz-15_19"
                >
                  ${strings.tagalys?.showAll ||
                    window.TagalysTranslations?.show_all ||
                    ''}
                </a>
              </div>
            `
          : html`
              <p class="fz-15_28">
                ${(strings.tagalys?.noResults || '').replace(
                  '{searchTerm}',
                  helpers.getEnteredSearchQuery(),
                )}
              </p>
            `}
      </div>
      <ul class=${className}>
        <${args.templates.products}
          className="tag-SearchSuggestions_Products"
        />
      </ul>
      <div class="tag-Title_Header">
        ${resultCount > 0
          ? html`
              <span
                class="tag-SearchSuggestions_Heading fz-15_19 tag-ResultTotal"
              >
                ${resultsLabel}
              </span>
              <div class="tag-Title_ViewAll">
                <a
                  href="${TagalysLibrary.getSearchResultsUrl(
                    helpers.getEnteredSearchQuery(),
                  )}"
                  target="_parent"
                  class="tag-Title_Button fz-15_19"
                >
                  ${strings.tagalys?.showAll ||
                    window.TagalysTranslations?.show_all ||
                    ''}
                </a>
              </div>
            `
          : ''}
      </div>
    `
  }

  static textSuggestionsSectionRender (html, args) {
    const props = args.props
    const textSuggestionSection = props.textSuggestionSection
    const sectionId =
      textSuggestionSection.id || textSuggestionSection.section_id
    const sectionTitle =
      textSuggestionSection.title || textSuggestionSection.section_title

    return html`
      <li class="text-suggestion-section" data-section-id=${sectionId}>
        <span class="section-title">
          ${sectionTitle}
        </span>
        <ul>
          ${textSuggestionSection.items.map(textSuggestionSectionItem => {
            return html`
              <${args.templates.textSuggestionSectionItem}
                textSuggestionSectionId=${sectionId}
                textSuggestionSectionItem=${textSuggestionSectionItem}
              />
            `
          })}
        </ul>
      </li>
    `
  }

  static quickLinkRender (html, args) {
    return html`
      <section class="tag-SearchSuggestions_QuickLinks">
        <header class="tag-SearchSuggestions_QuickLinksHeader fz-15_28">
          ${window.TagalysTranslations?.how_can_we_help}
        </header>

        <div class="tag-SearchSuggestions_QuickLinksItems">
          <div class="tag-SearchSuggestions_QuickLinksItem fz-15_28">
            <a
              href="${TagalysLibrary.getPageUrl('shipping')}"
              class="tag-SearchSuggestions_QuickLink"
            >
              ${window.BAO.Utils.parseHtmlEntities(
                window.TagalysTranslations?.shipping,
              )}
            </a>
          </div>

          <div class="tag-SearchSuggestions_QuickLinksItem fz-15_28">
            <a
              href="${TagalysLibrary.getPageUrl('returns')}"
              class="tag-SearchSuggestions_QuickLink"
              >${window.BAO.Utils.parseHtmlEntities(
                window.TagalysTranslations?.returns,
              )}</a
            >
          </div>

          <div class="tag-SearchSuggestions_QuickLinksItem fz-15_28">
            <a
              href="${TagalysLibrary.getPageUrl('store-listing')}"
              class="tag-SearchSuggestions_QuickLink"
              >${window.BAO.Utils.parseHtmlEntities(
                window.TagalysTranslations?.stores,
              )}</a
            >
          </div>

          <div class="tag-SearchSuggestions_QuickLinksItem fz-15_28">
            <a
              href="${TagalysLibrary.getPageUrl('faqs')}"
              class="tag-SearchSuggestions_QuickLink"
              >${window.BAO.Utils.parseHtmlEntities(
                window.TagalysTranslations?.faqs,
              )}</a
            >
          </div>

          <div class="tag-SearchSuggestions_QuickLinksItem fz-15_28">
            <a
              href="${TagalysLibrary.getPageUrl('contact')}"
              class="tag-SearchSuggestions_QuickLink"
              >${window.BAO.Utils.parseHtmlEntities(
                window.TagalysTranslations?.contact_us,
              )}</a
            >
          </div>
        </div>
      </section>
    `
  }

  static plainProductRender (product) {
    if (!product) return ''

    const onSale =
      product.compare_at_price_min !== product.price &&
      product.compare_at_price_min !== null &&
      product.compare_at_price_min !== 0
    const saleSticker = onSale
      ? `<p class="prd-Card_Sticker prd-Card_Sticker-sale fz-13_17">${window.theme.strings.stickerSale}</p>`
      : ''

    const badges = []
    product.tags?.forEach(tag => {
      if (tag.includes('plp-badge')) {
        const badgeParts = tag.split(':')
        badges.push(badgeParts[1])
      }
    })

    const productTitle = TagalysLibrary.getProductTitle(product)

    const colourTitle = TagalysLibrary.getColourTitle(product)

    const primaryImageSrc =
      TagalysLibrary.getProductImageUrl(product) ||
      product.images?.[0]?.src ||
      ''

    // Create unique container ID for dynamic color updates
    const colorContainerId = `plain-color-section-${Math.random()
      .toString(36)
      .substr(2, 9)}`

    let productPrice = BAO.Utils.formatMoney(
      product.price * 100,
      theme.moneyFormat,
    )

    if (product.price === 0) {
      productPrice = ''
    }

    const htmlResult = `
      <li class="tag-SearchSuggestions_Product">
        <product-card-gallery>
          <article class="prd-Card util-FauxLink" data-product-card-gallery-el="card">
            <div class="prd-Card_ImageContainer keen-slider" data-product-card-gallery-el="slides">
              <div class="prd-Card_Image prd-Card_Image-active keen-slider__slide util_FauxLink" data-product-card-gallery-el="slide">
                <div class="rsp-Image">
                  <img
                    class="rsp-Image_Image"
                    src=${primaryImageSrc}
                    alt="${productTitle}"
                  />
                </div>
                <a class="util-FauxLink_Link" href="${TagalysLibrary.getProductLink(
                  product,
                )}">
                  <span class="util-ScreenReaderOnly">
                    ${productTitle}
                  </span>
                </a>
              </div>

              ${product.images
                ?.slice(1, 4)
                .map(
                  image => /* html */ `
                    <div class="prd-Card_Image keen-slider__slide util_FauxLink" data-product-card-gallery-el="slide">
                      <div class="rsp-Image">
                        <img
                          class="rsp-Image_Image"
                          src="${image.src}"
                          alt="${productTitle}"
                        />
                      </div>
                      <a class="util-FauxLink_Link" href="${TagalysLibrary.getProductLink(
                        product,
                      )}">
                        <span class="util-ScreenReaderOnly">
                          ${productTitle}
                        </span>
                      </a>
                    </div>
                  `,
                )
                .join('') || ''}

              <button type="button" data-product-card-gallery-el="previous" class="prd-Card_NavButton prd-Card_NavButton-prev" aria-label="${
                strings.previousSlide
              }">
                <svg viewBox="0 0 5 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M4.32.499 1.316 3.502 4.32 6.506"
                    stroke="currentColor"
                    stroke-linecap="round"
                  />
                </svg>
                <label class="util-ScreenReaderOnly">
                  ${strings.previousSlide}
                </label>
              </button>

              <button type="button" data-product-card-gallery-el="next" class="prd-Card_NavButton prd-Card_NavButton-next" aria-label="${
                strings.nextSlide
              }">
                <svg viewBox="0 0 5 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="m.68 6.506 3.005-3.004L.68.5" stroke="currentColor" stroke-linecap="round" />
                </svg>
                <label class="util-ScreenReaderOnly">
                  ${strings.nextSlide}
                </label>
              </button>
            </div>

             ${saleSticker}

           <div class="prd-Card_DotContainer">
              <div class="prd-Card_Dots">
                ${product.images
                  ?.slice(1, 4)
                  .map(
                    (_, i) => /* html */ `
                 <button
                    class="prd-Card_Dot"
                    type="button"
                    aria-current="${i === 0}"
                    data-index="${i + 1}"
                    data-product-card-gallery-el="dot"
                  >
                    <span class="util-ScreenReaderOnly">
                      Dot
                    </span>
                  </button>
                `,
                  )
                  .join('')}
              </div>
            </div>

            <div class="prd-Card_Body util-FauxLink fz-15_21">
              <div class="prd-Card_Details">
                <h3 class="prd-Card_Title">
                  ${productTitle}
                </h3>

                <p class="prd-Card_Price ${
                  onSale ? 'prd-Card_Price-onsale' : ''
                }">
                    <span class="prd-Card_Price">${productPrice}</span>

                  <s class="prd-Card_RegularPrice">
                    <span class="util-ScreenReaderOnly">Regular price</span>
                    ${BAO.Utils.formatMoney(
                      product.compare_at_price * 100,
                      theme.moneyFormat,
                    )}
                  </s>

                  <span class="prd-Card_SalePrice">
                    ${productPrice}
                  </span>
                </p>
              </div>

            <div class="prd-Card_Colours" id="${colorContainerId}" ${
      colourTitle ? '' : 'hidden'
    }>
              <p class="prd-Card_Colour prd-Card_Colour-current fz-15_21">
                ${colourTitle}
              </p>
            </div>

            <ul class="prd-Card_BadgeList" ${badges.length ? '' : 'hidden'}>
              ${badges.map(badge => {
                return `
                  <li class="prd-Card_Badge fz-11_13">${badge}</li>
                `
              })}
            </ul>

            <a class="util-FauxLink_Link" href="${TagalysLibrary.getProductLink(
              product,
            )}">
              <span class="util-ScreenReaderOnly">
                ${productTitle}
              </span>
            </a>
          </div>

          </article>
        </product-card-gallery>
      </li>
    `

    // Initialize dynamic color counting using shared method
    TagalysLibrary.initializeColorCounting(product, colorContainerId)

    return htmlResult
  }

  static recentSearchItemRender (html, args) {
    const props = args.props
    const helpers = props.helpers
    const enteredSearchQuery = helpers.getEnteredSearchQuery() || ''
    const query =
      props.recentSearch.query || props.recentSearch.displayString || ''

    return html`
      <li class="fz-15_28">
        <a
          class="tag-SearchSuggestions_PopularResult tag-SearchSuggestions_RecentResult"
          href=${helpers.getLink(query)}
        >
          <${args.templates.highlighter}
            label=${query}
            highlight=${enteredSearchQuery}
          />
        </a>
      </li>
    `
  }

  static productRender (html, args) {
    const props = args.props
    const product = props.product
    if (!product) return ''

    const onSale =
      product.compare_at_price !== product.price &&
      product.compare_at_price !== null &&
      product.compare_at_price !== 0

    const saleSticker = onSale
      ? html`
          <p class="prd-Card_Sticker prd-Card_Sticker-sale fz-13_17 2">
            ${window.theme.strings.stickerSale}
          </p>
        `
      : ''

    const badges = []
    product.tags?.forEach(tag => {
      if (tag.includes('plp-badge')) {
        const badgeParts = tag.split(':')
        badges.push(badgeParts[1])
      }
    })

    const productTitle = TagalysLibrary.getProductTitle(product)

    const primaryImageSrc = TagalysLibrary.getProductImageUrl(product)

    let productPrice = BAO.Utils.formatMoney(
      product.price * 100,
      theme.moneyFormat,
    )

    if (product.price === 0) {
      productPrice = ''
    }

    return html`
      <li class="tag-SearchSuggestions_Product">
        <tagalys-product-card-gallery>
          <article
            class="prd-Card util-FauxLink"
            data-tagalys-product-card-gallery-el="card"
          >
            <div
              class="prd-Card_ImageContainer keen-slider"
              data-tagalys-product-card-gallery-el="slides"
            >
              <div
                class="prd-Card_Image prd-Card_Image-active keen-slider__slide util_FauxLink"
                data-tagalys-product-card-gallery-el="slide"
              >
                <div class="rsp-Image">
                  <img
                    class="rsp-Image_Image"
                    src=${primaryImageSrc}
                    alt="${productTitle}"
                  />
                </div>
                <a
                  class="util-FauxLink_Link"
                  href="${TagalysLibrary.getProductLink(product)}"
                >
                  <span class="util-ScreenReaderOnly">
                    ${productTitle}
                  </span>
                </a>
              </div>

              ${product.images?.slice(1, 4).map(
                image => html`
                  <div
                    class="prd-Card_Image keen-slider__slide util_FauxLink"
                    data-tagalys-product-card-gallery-el="slide"
                  >
                    <div class="rsp-Image">
                      <img
                        class="rsp-Image_Image product-image"
                        src="${image.src}"
                        alt="${productTitle}"
                      />
                    </div>
                    <a
                      class="util-FauxLink_Link"
                      href="${TagalysLibrary.getProductLink(product)}"
                    >
                      <span class="util-ScreenReaderOnly">
                        ${productTitle}
                      </span>
                    </a>
                  </div>
                `,
              )}

              <button
                type="button"
                data-tagalys-product-card-gallery-el="previous"
                class="prd-Card_NavButton prd-Card_NavButton-prev"
                aria-label="${strings.previousSlide}"
              >
                <svg
                  viewBox="0 0 5 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.32.499 1.316 3.502 4.32 6.506"
                    stroke="currentColor"
                    stroke-linecap="round"
                  />
                </svg>

                <label class="util-ScreenReaderOnly">
                  ${strings.previousSlide}
                </label>
              </button>

              <button
                type="button"
                data-tagalys-product-card-gallery-el="next"
                class="prd-Card_NavButton prd-Card_NavButton-next"
                aria-label="${strings.nextSlide}"
              >
                <svg
                  viewBox="0 0 5 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="m.68 6.506 3.005-3.004L.68.5"
                    stroke="currentColor"
                    stroke-linecap="round"
                  />
                </svg>

                <label class="util-ScreenReaderOnly">
                  ${strings.nextSlide}
                </label>
              </button>
            </div>

            ${saleSticker}

            <div class="prd-Card_DotContainer">
              <div class="prd-Card_Dots">
                ${product.images?.slice(1, 4).map((_, i) => {
                  return html`
                    <button
                      class="prd-Card_Dot"
                      type="button"
                      aria-current="${i === 0}"
                      data-index="${i + 1}"
                      data-tagalys-product-card-gallery-el="dot"
                    >
                      <span class="util-ScreenReaderOnly">
                        Dot
                      </span>
                    </button>
                  `
                })}
              </div>
            </div>

            <div class="prd-Card_Body fz-15_21 util-FauxLink">
              <div class="prd-Card_Details">
                <h3 class="prd-Card_Title">
                  ${productTitle}
                </h3>
                <p
                  class="prd-Card_Price ${onSale
                    ? 'prd-Card_Price-onsale'
                    : ''}"
                >
                  <span class="prd-Card_Price">
                    ${productPrice}
                  </span>

                  <s class="prd-Card_RegularPrice">
                    <span class="util-ScreenReaderOnly">Regular price</span>
                    ${BAO.Utils.formatMoney(
                      product.compare_at_price * 100,
                      theme.moneyFormat,
                    )}
                  </s>

                  <span class="prd-Card_SalePrice">
                    ${productPrice}
                  </span>
                </p>
              </div>

              ${TagalysLibrary.renderColorSection(html, product)}

              <ul class="prd-Card_BadgeList" ${badges.length ? '' : 'hidden'}>
                ${badges.map(badge => {
                  return html`
                    <li class="prd-Card_Badge fz-11_13">${badge}</li>
                  `
                })}
              </ul>

              <a
                class="util-FauxLink_Link"
                href="${TagalysLibrary.getProductLink(product)}"
              >
                <span class="util-ScreenReaderOnly">
                  ${productTitle}
                </span>
              </a>
            </div>
          </article>
        </tagalys-product-card-gallery>
      </li>
    `
  }

  // Shared method to handle dynamic color counting
  static initializeColorCounting (product, containerId) {
    // Get related product IDs from metafield
    const relatedColours = product.metafields?.pdp_swatch?.related_colours
    const productIds = relatedColours?.value

    if (!productIds || !Array.isArray(productIds)) {
      return // No related products, keep as is
    }

    // Fetch availability asynchronously and update the container
    TagalysLibrary.fetchProductsAvailability(productIds).then(
      availableProducts => {
        const availableRelatedCount = availableProducts.length
        const container = document.getElementById(containerId)

        if (container && availableRelatedCount > 1) {
          // Simple duplicate prevention - check if already has more colours element
          if (container.querySelector('.prd-Card_Colour-more')) {
            return
          }

          // Check for custom label from metafields
          const customLabel =
            product.metafields?.pdp_swatch?.related_products_type?.value
              ?.product_card_label

          let labelText
          if (customLabel) {
            labelText = customLabel.replace('[count]', availableRelatedCount)
          } else {
            labelText = `${availableRelatedCount} ${window.theme.strings.coloursAvailable}`
          }

          // Add the more colours element
          const moreColoursHtml = `<p class="prd-Card_Colour prd-Card_Colour-more fz-15_21">${labelText}</p>`
          container.insertAdjacentHTML('beforeend', moreColoursHtml)
        }
      },
    )
  }

  // Fetch product availability from Storefront API
  static async fetchProductsAvailability (productIds) {
    if (!productIds || !Array.isArray(productIds) || productIds.length === 0) {
      return []
    }

    // Convert numeric IDs to Shopify GIDs and create aliases for GraphQL query
    const aliases = productIds.map(
      (id, index) =>
        `product${index}: product(id: "gid://shopify/Product/${id}") { id availableForSale }`,
    )

    const query = `query GetProductsAvailability { ${aliases.join(' ')} }`

    try {
      const { data } = await window.BAO.Utils.fetchJSON(
        `/api/2024-01/graphql.json`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token':
              window.theme.storefrontAccessToken,
          },
          body: JSON.stringify({ query }),
        },
      )

      if (!data) return []

      // Extract availability from aliased results
      return Object.values(data).filter(
        product => product && product.availableForSale,
      )
    } catch (error) {
      console.warn('Failed to fetch product availability:', error)
      return []
    }
  }

  static renderColorSection (html, product) {
    const currentColour = TagalysLibrary.getColourTitle(product)
    if (!currentColour) return ''

    // Create container ID for dynamic color updates
    const containerId = `color-section-${Math.random()
      .toString(36)
      .substr(2, 9)}`

    // Initialize dynamic color counting using shared method
    TagalysLibrary.initializeColorCounting(product, containerId)

    return html`
      <div class="prd-Card_Colours" id="${containerId}">
        <p class="prd-Card_Colour prd-Card_Colour-current fz-15_21">
          ${currentColour}
        </p>
      </div>
    `
  }

  static popularSearchItemRender (html, args) {
    const props = args.props
    const helpers = props.helpers
    const enteredSearchQuery = helpers.getEnteredSearchQuery() || ''
    const query =
      props.popularSearch.query || props.popularSearch.displayString || ''

    return html`
      <li class="fz-15_28">
        <a
          class="tag-SearchSuggestions_PopularResult"
          href=${helpers.getLink(query)}
        >
          <${args.templates.highlighter}
            label=${query}
            highlight=${enteredSearchQuery}
          />
        </a>
      </li>
    `
  }
}

window.BAO.TagalysLibrary = TagalysLibrary
